import {useQuery} from "@tanstack/react-query";
import {getSubjects} from "api/subject.api.ts";
import {useFilters} from "context/FilterContext";
import {onlineTypeOptions} from "conts/options";
import {filterAllowedClasses} from "conts/allowedClasses";
import { userRoute } from "pages/user/user.routes";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Modal } from "ui/base/modal/Modal";
import { SelectFilter } from "ui/base/selectInput/SelectFilter";
import { SearchIcon } from "ui/icons";
import { defaultThemeColors } from "ui/theme";
import { DEFAULT_ONLINE_TYPE, getFiltersSearchDisabledMessage, isFiltersSearchDisabled, withSearchDefaults } from "./filterUtils";
import { LocationField } from "./LocationField";
import { Field, Label as FieldLabel, PillButton as BasePillButton, tokens } from "ui/blocks/guestMessaging/GuestUI";

export const MobileFiltersSection = ({ isCompact = false }) => {
    const navigate = useNavigate();
    const {filters, setFilters} = useFilters();
    const [showDisabledHint, setShowDisabledHint] = useState(false);
    const searchButtonWrapperRef = useRef(null);

    const {data: subjectData} = useQuery({
        queryKey: ["subjects"],
        queryFn: getSubjects,
    });

    const filteredSubjects = filterAllowedClasses(subjectData);
    
    const subjects =
        filteredSubjects?.map((subject) => ({
            value: subject.id,
            label: subject.title,
        })) || [];

    const [modalOpen, setModalOpen] = useState(false);
    const openModal = () => setModalOpen(true);
    const closeModal = () => {
        setShowDisabledHint(false);
        setModalOpen(false);
    };

    const online_type = (filters.online_type || DEFAULT_ONLINE_TYPE).value;
    const isSearchDisabled = isFiltersSearchDisabled(filters);
    const disabledMessage = getFiltersSearchDisabledMessage(filters);
    const isMobile = useMediaQuery({maxWidth: "768px"});

    useEffect(() => {
        if (!showDisabledHint) return;

        const timeoutId = setTimeout(() => {
            setShowDisabledHint(false);
        }, 1800);

        return () => clearTimeout(timeoutId);
    }, [showDisabledHint]);

    useEffect(() => {
        setShowDisabledHint(false);
    }, [filters.typeOfClass, filters.online_type, filters.location]);

    useEffect(() => {
        if (!showDisabledHint || !modalOpen) return;

        const hideIfOutsideButton = (event) => {
            const target = event.target;
            if (!searchButtonWrapperRef.current?.contains(target)) {
                setShowDisabledHint(false);
            }
        };

        document.addEventListener("touchstart", hideIfOutsideButton);
        document.addEventListener("mousedown", hideIfOutsideButton);

        return () => {
            document.removeEventListener("touchstart", hideIfOutsideButton);
            document.removeEventListener("mousedown", hideIfOutsideButton);
        };
    }, [showDisabledHint, modalOpen]);

    if (!isMobile) return null;

    return (
        <Wrapper>
            <Container>
                <SearchBar onClick={openModal} isCompact={isCompact}>
                    <SearchIconWrapper>
                        <SearchIcon color={defaultThemeColors.txt}  size={isCompact ? 14 : 20} />
                    </SearchIconWrapper>
                    <SearchTextContainer>
                        <SearchTitle isCompact={isCompact}>{filters.typeOfClass?.label || "Search for a class"}</SearchTitle>
                        {!isCompact && (
                            <SearchSubtitle>
                                {(filters.online_type || DEFAULT_ONLINE_TYPE).label}
                                {" · "}
                                {online_type === "offline" ? (filters.location || "Near you") : "Anywhere"}
                            </SearchSubtitle>
                        )}
                    </SearchTextContainer>
                </SearchBar>

                <Modal handleClose={closeModal} show={modalOpen} mobileFull minHeight={0}>
                    <Sheet>
                        <SheetTitle>Find a class</SheetTitle>
                        <Fields
                            onClick={() => setShowDisabledHint(false)}
                            onTouchStart={() => setShowDisabledHint(false)}
                        >
                            <Field>
                                <FieldLabel as="span">Class</FieldLabel>
                                <ClassInput>
                                    <SelectFilter
                                        borderColor="lightBlue"
                                        bgColor="white"
                                        rounded
                                        placeholder="Select Class"
                                        value={filters.typeOfClass}
                                        onChange={(subject) => {
                                            setShowDisabledHint(false);
                                            setFilters({typeOfClass: subject});
                                        }}
                                        options={subjects}
                                    />
                                </ClassInput>
                            </Field>

                            <Field>
                                <FieldLabel as="span" id="search-type-label">Lesson type</FieldLabel>
                                <Segmented role="radiogroup" aria-labelledby="search-type-label">
                                    {[DEFAULT_ONLINE_TYPE, ...onlineTypeOptions.filter((o) => o.value !== DEFAULT_ONLINE_TYPE.value)].map((option) => (
                                        <Segment
                                            key={option.value}
                                            type="button"
                                            role="radio"
                                            aria-checked={online_type === option.value}
                                            $active={online_type === option.value}
                                            onClick={() => {
                                                setShowDisabledHint(false);
                                                setFilters({online_type: option});
                                            }}
                                        >
                                            {option.label}
                                        </Segment>
                                    ))}
                                </Segmented>
                            </Field>

                            {online_type === "offline" && (
                                <Field>
                                    <FieldLabel as="span">Location</FieldLabel>
                                    <ClassInput>
                                        <LocationField
                                            variant="stacked"
                                            placeholder="City or zip code"
                                            onChange={() => setShowDisabledHint(false)}
                                        />
                                    </ClassInput>
                                </Field>
                            )}
                        </Fields>

                        <SearchButtonWrapper ref={searchButtonWrapperRef}>
                            {showDisabledHint && isSearchDisabled ? (
                                <DisabledHint role="status">{disabledMessage}</DisabledHint>
                            ) : null}
                            <PillButton
                                type="button"
                                $block
                                aria-disabled={isSearchDisabled}
                                $dim={isSearchDisabled}
                                onClick={() => {
                                    if (isSearchDisabled) {
                                        setShowDisabledHint(true);
                                        return;
                                    }
                                    const search = withSearchDefaults(filters);
                                    setFilters({online_type: search.online_type});
                                    navigate(userRoute.searchOnline, {state: search});
                                    closeModal();
                                }}
                            >
                                <SearchIcon color="#fff" size={16}/> Search
                            </PillButton>
                        </SearchButtonWrapper>
                    </Sheet>
                </Modal>
            </Container>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    width: 100%;
`;

const Container = styled.div`
    display: none;
    @media (max-width: 767px) {
        display: block;
    }
`;

const SearchBar = styled.div`
    display: flex;
    align-items: center;
    background: #ffffff;
    border-radius: 999px;
    padding: ${(props) => (props.isCompact ? "8px 14px" : "12px 20px")};
    min-height: ${(props) => (props.isCompact ? "auto" : "60px")};
    box-sizing: border-box;
    box-shadow: ${(props) => (props.isCompact ? "0 2px 15px rgba(0, 0, 0, 0.08)" : "0 8px 28px rgba(0, 0, 0, 0.18)")};
    border: ${(props) => (props.isCompact ? `2px solid ${defaultThemeColors.border}` : "0")};
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:active {
        transform: ${(props) => (props.isCompact ? "none" : "scale(0.99)")};
    }
`;

const SearchIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    //padding-left: 6px;
`;

const SearchTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    margin-left: 12px;

    & > span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const SearchTitle = styled.span`
    font-weight: ${(props) => (props.isCompact ? "400" : "600")};;
    color: ${defaultThemeColors.txt};
    font-size: ${(props) => (props.isCompact ? "13px" : "15px")};
`;

const SearchSubtitle = styled.span`
    color: ${tokens.muted};
    font-size: 13px;
    margin-top: 1px;
`;





const SearchButtonWrapper = styled.div`
    position: relative;
    margin-top: auto;
`;

const DisabledHint = styled.div`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: calc(100% + 8px);
    background: ${defaultThemeColors.txt};
    color: ${defaultThemeColors.white};
    font-size: 12px;
    padding: 8px 10px;
    border-radius: 8px;
    white-space: nowrap;
    z-index: 10;
`;

const Sheet = styled.div`
    display: flex;
    flex-direction: column;
    gap: 22px;
    min-height: 100%;
    font-family: var(--font-primary);
`;

const SheetTitle = styled.h2`
    margin: 0;
    padding-right: 40px; /* close button */
    font-size: 20px;
    font-weight: 600;
    line-height: 1.25;
    color: ${tokens.ink};
`;

const Fields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
`;

/* Class and location inputs in the booking-popup style (16px stops iOS zoom). */
const ClassInput = styled.div`
    --auto-complete-font-size: 16px;

    label {
        height: 50px;
        border-width: 1px;
        border-color: ${tokens.border};
        border-radius: 12px;
        padding-inline: 14px;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    label:focus-within {
        border-color: ${tokens.accent};
        box-shadow: 0 0 0 3px ${tokens.accentSoft};
    }

    input {
        font-size: 16px !important;
    }

    .auto-complete__menu {
        border-radius: 12px;
        overflow: hidden;
    }
`;

const Segmented = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;
    padding: 4px;
    border-radius: 12px;
    background: ${tokens.warm};
`;

const Segment = styled.button`
    height: 42px;
    border: 0;
    border-radius: 9px;
    font: inherit;
    font-size: 15px;
    font-weight: ${({$active}) => ($active ? 600 : 400)};
    color: ${({$active}) => ($active ? tokens.ink : tokens.muted)};
    background: ${({$active}) => ($active ? "#fff" : "transparent")};
    box-shadow: ${({$active}) => ($active ? "0 1px 3px rgba(16, 16, 40, 0.12)" : "none")};
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
`;

const PillButton = styled(BasePillButton)`
    gap: 8px;
    opacity: ${({$dim}) => ($dim ? 0.45 : 1)};
`;
